/**
 * Hook pour gérer les écouteurs WebSocket du Dashboard
 * @module dashboard/hooks/useSocketListeners
 */

import { useState, useEffect, useCallback } from 'react';
import socketService from '../../../services/SocketService';
import { SOCKET_EVENTS, SOCKET_NAMESPACES } from '../../../config/constants';

/**
 * Hook de gestion des écouteurs WebSocket
 * @param {Object} activeTabRef - Ref de l'onglet actif
 * @param {Object} [options] - Options optionnelles
 * @param {Function} [options.onTwitchReauthRequired] - Callback (userId) appelé quand le backend émet twitch:reauth_required (redirection OAuth)
 * @returns {Object} État des notifications (mentions, channels suivis)
 */
export const useSocketListeners = (activeTabRef, options = {}) => {
  const { onTwitchReauthRequired } = options;
  const [hasNewMentions, setHasNewMentions] = useState(false);
  const [hasNewFollowedChannelOnline, setHasNewFollowedChannelOnline] =
    useState(false);

  useEffect(() => {
    // Se connecter au namespace WS-CHAT pour les mentions
    const chatSocket = socketService.connectToChat();
    // Se connecter au namespace WS-FOLLOWED pour les chaînes suivies
    const followedSocket = socketService.connectToFollowed();

    const handleNewMention = (mention) => {
      console.log('Nouvelle mention reçue:', mention);
      // Ne pas afficher la pastille si on est déjà sur l'onglet mentions
      if (activeTabRef.current !== 'mentions') {
        setHasNewMentions(true);
      }
    };

    const handleChannelOnline = (data) => {
      console.log('Channel suivi en ligne :', data);
      if (activeTabRef.current !== 'followed') {
        setHasNewFollowedChannelOnline(true);
      }
    };

    const handleTwitchReauthRequired = (data) => {
      if (typeof onTwitchReauthRequired === 'function' && data?.userId != null) {
        onTwitchReauthRequired(data.userId);
      }
    };

    if (chatSocket) {
      chatSocket.on(SOCKET_EVENTS.CHAT_MENTION, handleNewMention);
    }

    if (followedSocket) {
      followedSocket.on(SOCKET_EVENTS.CHAN_ONLINE, handleChannelOnline);
      followedSocket.on(SOCKET_EVENTS.TWITCH_REAUTH_REQUIRED, handleTwitchReauthRequired);
    }

    return () => {
      if (chatSocket) {
        chatSocket.off(SOCKET_EVENTS.CHAT_MENTION, handleNewMention);
      }
      if (followedSocket) {
        followedSocket.off(SOCKET_EVENTS.CHAN_ONLINE, handleChannelOnline);
        followedSocket.off(SOCKET_EVENTS.TWITCH_REAUTH_REQUIRED, handleTwitchReauthRequired);
      }
      // Déconnexion complète des namespaces
      socketService.disconnectFromNamespace(SOCKET_NAMESPACES.CHAT);
      socketService.disconnectFromNamespace(SOCKET_NAMESPACES.FOLLOWED);
    };
  }, [activeTabRef, onTwitchReauthRequired]);

  /**
   * Réinitialise la pastille des mentions
   */
  const resetMentionsBadge = useCallback(() => {
    setHasNewMentions(false);
  }, []);

  /**
   * Réinitialise la pastille des channels suivis
   */
  const resetFollowedBadge = useCallback(() => {
    setHasNewFollowedChannelOnline(false);
  }, []);

  return {
    hasNewMentions,
    hasNewFollowedChannelOnline,
    resetMentionsBadge,
    resetFollowedBadge,
  };
};
